import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Search, Plus, Pencil, Trash2, X, ShoppingCart, PackageCheck, Truck, Clock,
  User, MapPin, Package, CreditCard, Receipt, StickyNote, Phone, Mail, Minus, Gift, Sparkles, ExternalLink,
  Loader, XCircle, ChevronDown, Check,
} from 'lucide-react'
import { Modal, ConfirmModal } from '../../common/Modal/Modal'
import Select, { Pagination } from '../../common/Select/Select'
import {
  fetchOrders,
  createOrder,
  updateOrder,
  deleteOrder,
  clearOrderError,
} from './services/orderSlice'
import {
  fetchCouriers,
  createCourier,
  deleteCourier,
  clearCourierError,
} from './services/courierSlice'
import { fetchProducts } from '../Product/services/productSlice'

const STATUS_BADGE = {
  delivered: 'badge-active',
  shipped: 'badge-gold',
  processing: 'badge-pending',
  pending: 'badge-inactive',
  cancelled: 'badge-danger',
}

const STATUS_OPTIONS = ['pending', 'processing', 'shipped', 'delivered', 'cancelled']
  .map((v) => ({ value: v, label: v }))

// order status: icon + colour (used in the table dropdown)
const STATUS_META = {
  pending:    { icon: Clock,       color: '#6B7280' },
  processing: { icon: Loader,      color: '#4F46E5' },
  shipped:    { icon: Truck,       color: '#F59E0B' },
  delivered:  { icon: PackageCheck, color: '#10B981' },
  cancelled:  { icon: XCircle,     color: '#EF4444' },
}

const PAYMENT_BADGE = {
  paid: 'badge-active',
  processing: 'badge-pending',
  failed: 'badge-danger',
  refunded: 'badge-gold',
  pending: 'badge-inactive',
}

const METHOD_OPTIONS = [
  { value: 'cod', label: 'Cash on delivery' },
  { value: 'upi', label: 'UPI' },
  { value: 'card', label: 'Card' },
  { value: 'netbanking', label: 'Net banking' },
]
const METHOD_LABEL = Object.fromEntries(METHOD_OPTIONS.map((m) => [m.value, m.label]))

const formatPrice = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`
const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—')

const EMPTY_ITEM = { product_id: '', quantity: 1, customized: false, gift_wrap: false, custom_text: '' }
const EMPTY_FORM = {
  customer_name: '', customer_email: '', customer_phone: '',
  shipping_address: '', shipping_city: '', shipping_state: '', shipping_pincode: '',
  payment_method: 'cod', payment_status: 'pending', status: 'pending',
  shipping_charge: 0, discount: 0, notes: '',
  courier_agency_id: '', tracking_no: '',
  items: [{ ...EMPTY_ITEM }],
}

/* ── small building blocks for the order modals ── */
function Section({ icon: Icon, title, hint, className = '', children }) {
  return (
    <section className={`om-sec ${className}`}>
      <h4 className="om-sec-title">
        <span className="om-sec-ic"><Icon size={14} /></span>
        {title}
        {hint && <small>{hint}</small>}
      </h4>
      {children}
    </section>
  )
}

function SummaryRows({ rows, total }) {
  return (
    <div className="om-sum">
      {rows.filter((r) => r.show !== false).map((r) => (
        <div key={r.label} className="om-sum-row">
          <span>{r.label}</span>
          <span>{r.value}</span>
        </div>
      ))}
      <div className="om-sum-row om-sum-total">
        <span>Total</span>
        <span>{total}</span>
      </div>
    </div>
  )
}

/* ── order status: badge-style button + dropdown menu (to change status directly from the table) ── */
function StatusMenu({ status, onChange, disabled }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, up: false })
  const btnRef = useRef(null)
  const menuRef = useRef(null)

  const meta = STATUS_META[status] || STATUS_META.pending
  const Icon = meta.icon

  const toggle = () => {
    if (disabled) return
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const up = window.innerHeight - r.bottom < 280 && r.top > 280
      setPos({ left: r.left, top: up ? r.top - 6 : r.bottom + 6, up })
    }
    setOpen((o) => !o)
  }

  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return
      close()
    }
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  const pick = (v) => {
    setOpen(false)
    if (v !== status) onChange(v)
  }

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`badge st-btn ${STATUS_BADGE[status] || 'badge-inactive'}${open ? ' open' : ''}`}
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change order status"
      >
        <Icon size={12} strokeWidth={2.2} />
        {status}
        {!disabled && <ChevronDown size={12} className="st-chev" />}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          className="st-menu"
          style={{
            left: pos.left,
            top: pos.top,
            transform: pos.up ? 'translateY(-100%)' : undefined,
          }}
        >
          <div className="st-menu-head">Change order status</div>
          {STATUS_OPTIONS.map((o) => {
            const m = STATUS_META[o.value]
            const OptIcon = m.icon
            const current = o.value === status
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={current}
                className={`st-opt${current ? ' current' : ''}${o.value === 'cancelled' ? ' danger' : ''}`}
                onClick={() => pick(o.value)}
              >
                <span className="st-ic" style={{ background: `${m.color}1f`, color: m.color }}>
                  <OptIcon size={13} strokeWidth={2.2} />
                </span>
                {o.label}
                {current && <Check size={14} className="st-check" />}
              </button>
            )
          })}
        </div>,
        document.body
      )}
    </>
  )
}

/* ── everything the customer filled on the product page (text, image, position, names, charms) ── */
function CustomDetails({ it }) {
  const fields = Array.isArray(it.custom_fields) ? it.custom_fields : []
  const has = it.is_customized || it.custom_text || it.custom_logo || fields.length > 0 || it.addon_ferrero
  if (!has) return null

  const scale = Number(it.text_scale) || 1
  const textStyle = {
    left: `${Number(it.text_x ?? 50)}%`,
    top: `${Number(it.text_y ?? 46)}%`,
    color: it.text_color || '#222',
    fontSize: `${Math.round(12 * scale)}px`,
  }

  return (
    <div className="om-cust">
      <div className="om-cust-head"><Sparkles size={12} /> Customisation details</div>

      {(it.custom_text || it.custom_logo) && (
        <div className="om-cust-figs">
          {it.custom_text && (
            <figure className="om-cust-fig">
              <div className="om-cust-stage">
                {it.base_image && <img src={it.base_image} alt="" />}
                <span className="om-cust-text" style={textStyle}>{it.custom_text}</span>
              </div>
              <figcaption>Text placement</figcaption>
            </figure>
          )}
          {it.custom_logo && (
            <figure className="om-cust-fig">
              <a className="om-cust-stage" href={it.custom_logo} target="_blank" rel="noreferrer">
                <img src={it.custom_logo} alt="" style={{ objectFit: 'contain' }} />
              </a>
              <figcaption>
                {it.custom_image_name || 'Uploaded image'} ·{' '}
                <a href={it.custom_logo} download target="_blank" rel="noreferrer">Download</a>
              </figcaption>
            </figure>
          )}
        </div>
      )}

      <div className="om-cust-rows">
        {it.custom_text && (
          <div className="om-cust-row"><span>Text</span><b>{it.custom_text}</b></div>
        )}
        {it.custom_text && (
          <div className="om-cust-row">
            <span>Text position</span>
            <b>
              X {Number(it.text_x ?? 50)}% · Y {Number(it.text_y ?? 46)}% · size {Math.round(scale * 100)}%
              {it.text_color && <i className="om-cust-sw" style={{ background: it.text_color }} title={it.text_color} />}
              {it.text_color && ` ${it.text_color}`}
            </b>
          </div>
        )}
        {it.name_mode && fields.length > 0 && (
          <div className="om-cust-row">
            <span>Name option</span>
            <b>{it.name_mode === 'different' ? 'Different name on each product' : 'Same name on all products'}</b>
          </div>
        )}
        {fields.map((f, i) => (
          <div key={`${f.label}-${i}`} className="om-cust-row"><span>{f.label}</span><b>{f.value}</b></div>
        ))}
        {it.addon_ferrero && (
          <div className="om-cust-row"><span>Add-on</span><b>Ferrero Rocher (Pack of 4)</b></div>
        )}
      </div>
    </div>
  )
}

export default function Order() {
  const dispatch = useDispatch()
  const { list: orders, isLoading, isSaving, isError, message, errors } =
    useSelector((state) => state.order)
  const products = useSelector((state) => state.product.list)
  const {
    list: couriers,
    isSaving: courierSaving,
    isError: courierIsError,
    message: courierMessage,
  } = useSelector((state) => state.courier)

  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const [createOpen, setCreateOpen] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [shipOpen, setShipOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [shipOrder, setShipOrder] = useState(null)
  const [shipForm, setShipForm] = useState({ courier_agency_id: '', tracking_no: '' })
  const [shipError, setShipError] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [formError, setFormError] = useState('')
  const [edit, setEdit] = useState({
    status: 'pending', courier_agency_id: '', tracking_no: '', notes: '',
  })
  const [newCourier, setNewCourier] = useState({ name: '', tracking_url: '' })

  useEffect(() => {
    dispatch(fetchOrders())
    dispatch(fetchProducts())
    dispatch(fetchCouriers())
  }, [dispatch])

  /* ── filter + pagination ── */
  const filtered = useMemo(() => orders.filter((o) => {
    const q = search.toLowerCase()
    const matchesSearch =
      o.customer_name.toLowerCase().includes(q) ||
      o.order_no.toLowerCase().includes(q) ||
      (o.customer_phone || '').includes(q) ||
      (o.tracking_no || '').toLowerCase().includes(q)
    const matchesStatus = filter === 'all' ? true : o.status === filter
    return matchesSearch && matchesStatus
  }), [orders, search, filter])

  useEffect(() => { setPage(1) }, [search, filter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  /* ── helpers ── */
  const fieldError = (name) =>
    errors?.[name] ? <small style={{ color: '#ef4444' }}>{errors[name][0]}</small> : null

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }))
  const setItem = (i, key, value) =>
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)) }))
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }))
  const removeItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((_, idx) => idx !== i) : f.items }))

  const productMap = useMemo(() => new Map(products.map((p) => [p.id, p])), [products])

  const productOptions = useMemo(
    () => [
      { value: '', label: '— Select product —' },
      ...products.filter((p) => p.status === 1 && p.in_stock)
        .map((p) => ({ value: p.id, label: `${p.name} (${formatPrice(p.price)})` })),
    ],
    [products]
  )

  const courierOptions = useMemo(
    () => [
      { value: '', label: '— No courier —' },
      ...couriers.map((c) => ({ value: c.id, label: c.name })),
    ],
    [couriers]
  )

  // no "No courier" option in the shipping popup (courier is mandatory)
  const shipCourierOptions = useMemo(
    () => couriers.map((c) => ({ value: c.id, label: c.name })),
    [couriers]
  )

  // live preview (server final price pharse calculate kare chhe) - GST + MRP discount sahit
  const preview = useMemo(() => {
    const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100
    const lines = []

    form.items.forEach((it) => {
      const p = productMap.get(Number(it.product_id))
      if (!p) return
      const q = Math.max(1, Number(it.quantity) || 1)
      const unit = Number(p.price)
      const cust = it.customized ? Number(p.customization_price || 0) : 0
      const wrap = it.gift_wrap ? Number(p.gift_wrap_price || 0) : 0
      const mrp = p.old_price && Number(p.old_price) > unit ? Number(p.old_price) : unit
      lines.push({
        q, unit, cust, wrap, mrp,
        line: (unit + cust + wrap) * q,
        rate: Number(p.gst_rate || 0),
        inclusive: p.price_includes_tax !== false,
      })
    })

    const gross = lines.reduce((a, l) => a + l.line, 0)
    const shipping = Number(form.shipping_charge || 0)
    const discount = Math.max(0, Math.min(Number(form.discount || 0), gross))

    let units = 0, subtotal = 0, custom = 0, gift = 0, tax = 0, taxAdded = 0, savings = 0
    lines.forEach((l) => {
      const share = gross > 0 ? discount * (l.line / gross) : 0
      const payable = l.line - share
      if (l.inclusive) {
        tax += payable - payable / (1 + l.rate / 100)
      } else {
        const t = (payable * l.rate) / 100
        tax += t
        taxAdded += t
      }
      units += l.q
      subtotal += l.unit * l.q
      custom += l.cust * l.q
      gift += l.wrap * l.q
      savings += (l.mrp - l.unit) * l.q
    })

    return {
      units,
      subtotal: r2(subtotal), custom: r2(custom), gift: r2(gift),
      shipping, discount: r2(discount),
      tax: r2(tax), taxAdded: r2(taxAdded), savings: r2(savings),
      total: r2(gross - discount + taxAdded + shipping),
    }
  }, [form.items, form.shipping_charge, form.discount, productMap])

  const openCreate = () => {
    dispatch(clearOrderError())
    setFormError('')
    setForm({ ...EMPTY_FORM, items: [{ ...EMPTY_ITEM }] })
    setCreateOpen(true)
  }

  const openView = (o) => {
    dispatch(clearOrderError())
    setActive(o)
    setEdit({
      status: o.status,
      courier_agency_id: o.courier_agency_id || '',
      tracking_no: o.tracking_no || '',
      notes: o.notes || '',
    })
    setViewOpen(true)
  }

  /* ── courier management ── */
  const openManage = () => {
    dispatch(clearCourierError())
    setNewCourier({ name: '', tracking_url: '' })
    setManageOpen(true)
  }

  const addCourier = async () => {
    if (!newCourier.name.trim()) return
    const result = await dispatch(createCourier({
      name: newCourier.name.trim(),
      tracking_url: newCourier.tracking_url.trim() || null,
    }))
    if (result.meta.requestStatus === 'fulfilled') setNewCourier({ name: '', tracking_url: '' })
  }

  const removeCourier = (c) => {
    if (window.confirm(`Delete "${c.name}"? Existing orders will keep their tracking link.`)) {
      dispatch(deleteCourier(c.id))
    }
  }

  /* ── save / delete ── */
  const saveCreate = async () => {
    setFormError('')
    if (!form.customer_name.trim()) {
      setFormError('Enter the customer name.')
      return
    }
    const items = form.items
      .filter((it) => it.product_id !== '')
      .map((it) => ({
        product_id: Number(it.product_id),
        quantity: Number(it.quantity) || 1,
        customized: it.customized ? 1 : 0,
        gift_wrap: it.gift_wrap ? 1 : 0,
        custom_text: it.custom_text || null,
      }))
    if (items.length === 0) {
      setFormError('Add at least one product.')
      return
    }
    if (form.tracking_no.trim() && !form.courier_agency_id) {
      setFormError('Select the courier agency for this tracking number.')
      return
    }

    const payload = {
      ...form,
      courier_agency_id: form.courier_agency_id || null,
      tracking_no: form.tracking_no.trim() || null,
      shipping_charge: Number(form.shipping_charge) || 0,
      discount: Number(form.discount) || 0,
      items,
    }
    const result = await dispatch(createOrder(payload))
    if (result.meta.requestStatus === 'fulfilled') {
      setCreateOpen(false)
      dispatch(fetchProducts())   // stock refresh
    }
  }

  const saveEdit = async () => {
    if (edit.tracking_no.trim() && !edit.courier_agency_id) {
      window.alert('Select the courier agency for this tracking number.')
      return
    }
    const result = await dispatch(updateOrder({
      id: active.id,
      ...edit,
      courier_agency_id: edit.courier_agency_id || null,
      tracking_no: edit.tracking_no.trim(),
    }))
    if (result.meta.requestStatus === 'fulfilled') {
      setActive(result.payload)
      setViewOpen(false)
      dispatch(fetchProducts())   // stock is restored if the order was cancelled
    }
  }

  // change order status directly from the table
  const changeStatus = async (o, value) => {
    if (value === o.status) return

    // When Shipped is selected, first open a popup to ask for courier + tracking number
    if (value === 'shipped') {
      dispatch(clearOrderError())
      setShipError('')
      setShipOrder(o)
      setShipForm({
        courier_agency_id: o.courier_agency_id || '',
        tracking_no: o.tracking_no || '',
      })
      setShipOpen(true)
      return
    }

    if (value === 'cancelled' &&
        !window.confirm(`Cancel ${o.order_no}? Stock will be added back and the order cannot be reopened.`)) {
      return
    }

    const result = await dispatch(updateOrder({ id: o.id, status: value }))
    if (result.meta.requestStatus === 'fulfilled') {
      dispatch(fetchProducts())   // refresh stock if the order is cancelled
    }
  }

  // shipping popup: save status = shipped together with courier + tracking
  const saveShipping = async () => {
    setShipError('')
    if (!shipForm.courier_agency_id) {
      setShipError('Select the courier agency.')
      return
    }
    if (!shipForm.tracking_no.trim()) {
      setShipError('Enter the tracking number.')
      return
    }

    const result = await dispatch(updateOrder({
      id: shipOrder.id,
      status: 'shipped',
      courier_agency_id: shipForm.courier_agency_id,
      tracking_no: shipForm.tracking_no.trim(),
    }))
    if (result.meta.requestStatus === 'fulfilled') {
      setShipOpen(false)
      setShipOrder(null)
    }
  }

  const onDelete = async () => {
    const result = await dispatch(deleteOrder(active.id))
    if (result.meta.requestStatus === 'fulfilled') {
      setDeleteOpen(false)
      setActive(null)
      dispatch(fetchProducts())   // stock is restored on delete
    }
  }

  /* ── stats ── */
  const count = (s) => orders.filter((o) => o.status === s).length
  const pct = (n) => (orders.length ? Math.round((n / orders.length) * 100) : 0)

  const stats = [
    { label: 'Total Orders', value: orders.length, icon: ShoppingCart, color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', variant: 'indigo' },
    { label: 'Delivered', value: count('delivered'), icon: PackageCheck, color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', variant: 'green' },
    { label: 'Shipped', value: count('shipped'), icon: Truck, color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', variant: 'amber' },
    { label: 'Pending', value: count('pending'), icon: Clock, color: '#EF4444', colorGlow: 'rgba(239,68,68,0.15)', variant: 'red' },
  ]

  return (
    <div className="dash-page page-in">
      {/* ── PAGE HEADER ── */}
      <div className="pg-header">
        <h1 className="page-title" style={{ margin: 0 }}>Orders</h1>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={14} /> Add Order</button>
      </div>

      {/* ── STATS ── */}
      <div className="stats-grid cols-4">
        {stats.map((s) => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{
              position: 'absolute', top: 0, right: 0, width: 110, height: 110,
              borderRadius: '50%', background: s.colorGlow, filter: 'blur(30px)',
              transform: 'translate(30%, -30%)', pointerEvents: 'none',
            }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.label === 'Total Orders' ? '100%' : `${pct(s.value)}%`}
              </span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, height: 2,
              background: `linear-gradient(90deg,${s.color}88,transparent)`,
              borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
            }} />
          </div>
        ))}
      </div>

      {/* ── TABLE ── */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <label className="tb-search">
            <Search size={15} aria-hidden="true" />
            <input
              type="text"
              placeholder="Search order, name, phone or tracking…"
              aria-label="Search orders"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="view-tabs" style={{ margin: 0 }}>
            {['all', 'pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((f) => (
              <button key={f} className={`vtab${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order ID</th><th>Customer</th><th>Items</th><th>Total</th>
                <th>Payment</th><th>Date</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 'var(--space-lg)', color: 'var(--text3)' }}>No orders found</td></tr>
              )}
              {paginated.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{o.order_no}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text1)' }}>{o.customer_name}</div>
                    <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{o.customer_phone || o.customer_email || ''}</div>
                  </td>
                  <td>{o.items_count} {o.items_count === 1 ? 'item' : 'items'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', color: 'var(--color-green)' }}>{formatPrice(o.total)}</td>
                  <td>
                    <span className={`badge ${PAYMENT_BADGE[o.payment_status] || 'badge-inactive'}`}>
                      {o.payment_status}
                    </span>
                  </td>
                  <td style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{formatDate(o.created_at)}</td>
                  <td>
                    <StatusMenu
                      status={o.status}
                      onChange={(v) => changeStatus(o, v)}
                      disabled={isSaving || o.status === 'cancelled'}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openView(o)} aria-label="Open order"><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(o); setDeleteOpen(true) }} aria-label="Delete order"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isLoading && filtered.length > 0 && (
          <Pagination
            total={filtered.length}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* ══════════ SHIPPING DETAILS (when Shipped is selected in the table) ══════════ */}
      <Modal
        open={shipOpen}
        title="Shipping details"
        subtitle={shipOrder ? `Mark ${shipOrder.order_no} as shipped` : ''}
        onClose={() => setShipOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShipOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveShipping} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Mark as Shipped'}
            </button>
          </>
        )}
      >
        {shipError && <div className="login-error" style={{ marginBottom: 12 }}>{shipError}</div>}
        {isError && message && <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>}

        {shipOrder && (
          <div className="om-stack">
            <div className="om-hero">
              <div className="om-hero-badges">
                <span className="om-hero-method">{shipOrder.customer_name}</span>
                <span className="om-hero-method">
                  {shipOrder.items_count} {shipOrder.items_count === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div className="om-hero-total">
                <small>Order total</small>
                <b>{formatPrice(shipOrder.total)}</b>
              </div>
            </div>

            <Section icon={Truck} title="Courier & tracking">
              <div className="om-grid">
                <div className="form-field">
                  <label>
                    Courier name
                    <button type="button" className="om-link-btn" onClick={openManage}>Manage couriers</button>
                  </label>
                  <Select
                    value={shipForm.courier_agency_id}
                    onChange={(v) => setShipForm({ ...shipForm, courier_agency_id: v })}
                    options={shipCourierOptions}
                    searchable={shipCourierOptions.length > 8}
                    placeholder="— Select courier —"
                  />
                  {fieldError('courier_agency_id')}
                </div>
                <div className="form-field">
                  <label>Tracking number</label>
                  <input
                    className="input"
                    placeholder="Courier tracking number"
                    value={shipForm.tracking_no}
                    onChange={(e) => setShipForm({ ...shipForm, tracking_no: e.target.value })}
                  />
                  {fieldError('tracking_no')}
                </div>
              </div>
            </Section>
          </div>
        )}
      </Modal>

      {/* ══════════ VIEW / UPDATE MODAL ══════════ */}
      <Modal
        open={viewOpen}
        title={active ? `Order ${active.order_no}` : 'Order'}
        subtitle={active ? `Placed on ${formatDate(active.created_at)} by ${active.customer_name}` : ''}
        onClose={() => setViewOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setViewOpen(false)}>Close</button>
            <button className="btn btn-primary" onClick={saveEdit} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Update Order'}
            </button>
          </>
        )}
      >
        {isError && message && <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>}

        {active && (
          <div className="om-stack">

            {/* status strip */}
            <div className="om-hero">
              <div className="om-hero-badges">
                <span className={`badge ${STATUS_BADGE[active.status]}`}>{active.status}</span>
                <span className={`badge ${PAYMENT_BADGE[active.payment_status] || 'badge-inactive'}`}>
                  payment {active.payment_status}
                </span>
                <span className="om-hero-method">{METHOD_LABEL[active.payment_method] || active.payment_method}</span>
                {active.courier_name && (
                  <span className="om-hero-method">
                    {active.courier_name}{active.tracking_no ? ` · ${active.tracking_no}` : ''}
                  </span>
                )}
              </div>
              <div className="om-hero-total">
                <small>Order total</small>
                <b>{formatPrice(active.total)}</b>
              </div>
            </div>

            {/* items */}
            <Section icon={Package} title="Items" hint={`${active.items_count} ${active.items_count === 1 ? 'unit' : 'units'}`}>
              <div className="om-items">
                {active.items.map((it) => (
                  <div key={it.id} className="om-item">
                    {it.image ? <img src={it.image} alt="" /> : <span className="om-noimg"><Package size={16} /></span>}
                    <div className="om-item-main">
                      <div className="om-item-name">{it.product_name}</div>
                      <div className="om-item-sub">
                        {it.sku ? `${it.sku} · ` : ''}Qty {it.quantity} × {formatPrice(it.unit_price)}
                      </div>
                      {it.gst_rate != null && (
                        <div className="om-item-sub">
                          {it.hsn_code ? `HSN ${it.hsn_code} · ` : ''}
                          GST {Number(it.gst_rate)}% ({it.tax_inclusive ? 'incl.' : 'extra'}) · Tax {formatPrice(it.tax_amount)}
                          {Number(it.mrp) > Number(it.unit_price) ? ` · MRP ${formatPrice(it.mrp)}` : ''}
                        </div>
                      )}
                      {(it.customization_price > 0 || it.gift_wrap_price > 0 || it.custom_text || it.color) && (
                        <div className="om-chips">
                          {it.color && <span className="om-chip">Colour: {it.color}</span>}
                          {it.customization_price > 0 && (
                            <span className="om-chip"><Sparkles size={11} /> Personalised +{formatPrice(it.customization_price)}</span>
                          )}
                          {it.custom_text && <span className="om-chip">“{it.custom_text}”</span>}
                          {it.gift_wrap_price > 0 && (
                            <span className="om-chip"><Gift size={11} /> Gift wrap +{formatPrice(it.gift_wrap_price)}</span>
                          )}
                        </div>
                      )}
                      <CustomDetails it={it} />
                    </div>
                    <div className="om-item-total">{formatPrice(it.line_total)}</div>
                  </div>
                ))}
              </div>
            </Section>

            {/* customer + delivery */}
            <div className="om-two">
              <Section icon={User} title="Customer">
                <div className="om-info">
                  <div className="om-info-name">{active.customer_name}</div>
                  {active.customer_phone && <div className="om-info-row"><Phone size={13} /> {active.customer_phone}</div>}
                  {active.customer_email && <div className="om-info-row"><Mail size={13} /> {active.customer_email}</div>}
                </div>
              </Section>

              <Section icon={MapPin} title="Delivery address">
                <div className="om-info">
                  {[active.shipping_address, active.shipping_city, active.shipping_state, active.shipping_pincode].filter(Boolean).length
                    ? (
                      <>
                        {active.shipping_address && <div>{active.shipping_address}</div>}
                        <div className="om-muted">
                          {[active.shipping_city, active.shipping_state, active.shipping_pincode].filter(Boolean).join(', ')}
                        </div>
                      </>
                    )
                    : <div className="om-muted">No address added</div>}
                </div>
              </Section>
            </div>

            {/* price summary */}
            <Section icon={Receipt} title="Price summary">
              <SummaryRows
                rows={[
                  { label: 'Subtotal', value: formatPrice(active.subtotal) },
                  { label: 'Personalisation', value: formatPrice(active.customization_total), show: active.customization_total > 0 },
                  { label: 'Gift wrap', value: formatPrice(active.gift_wrap_total), show: active.gift_wrap_total > 0 },
                  { label: 'Shipping', value: active.shipping_charge > 0 ? formatPrice(active.shipping_charge) : 'Free' },
                  { label: 'Discount', value: `−${formatPrice(active.discount)}`, show: active.discount > 0 },
                  { label: 'GST (included)', value: formatPrice(Math.max(0, active.tax_total - active.tax_added)), show: active.tax_total - active.tax_added > 0 },
                  { label: 'GST (extra)', value: formatPrice(active.tax_added), show: active.tax_added > 0 },
                ]}
                total={formatPrice(active.total)}
              />
            </Section>

            {/* update */}
            <Section icon={Truck} title="Update order" className="om-sec--update">
              <div className="om-grid">
                <div className="form-field">
                  <label>Order status</label>
                  <Select value={edit.status} onChange={(v) => setEdit({ ...edit, status: v })} options={STATUS_OPTIONS} />
                  {fieldError('status')}
                </div>
                <div className="form-field">
                  <label>Payment status</label>
                  <input
                    className="input"
                    value={active.payment_status || ''}
                    disabled
                    readOnly
                    aria-label="Payment status"
                    style={{ textTransform: 'capitalize', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-field">
                  <label>
                    Courier agency
                    <button type="button" className="om-link-btn" onClick={openManage}>Manage couriers</button>
                  </label>
                  <Select
                    value={edit.courier_agency_id}
                    onChange={(v) => setEdit({ ...edit, courier_agency_id: v })}
                    options={courierOptions}
                    searchable={courierOptions.length > 8}
                    placeholder="— Select courier —"
                  />
                  {fieldError('courier_agency_id')}
                </div>
                <div className="form-field">
                  <label>Tracking number</label>
                  <input className="input" placeholder="Courier tracking number" value={edit.tracking_no}
                    onChange={(e) => setEdit({ ...edit, tracking_no: e.target.value })} />
                  {fieldError('tracking_no')}
                  {active.tracking_url && (
                    <a className="om-track-link" href={active.tracking_url} target="_blank" rel="noreferrer">
                      <ExternalLink size={12} /> Track on {active.courier_name || 'courier'} site
                    </a>
                  )}
                </div>
                <div className="form-field om-full">
                  <label>Notes</label>
                  <textarea className="input" rows={2} placeholder="Internal notes about this order" value={edit.notes}
                    onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
                </div>
              </div>
            </Section>
          </div>
        )}
      </Modal>

      {/* ══════════ CREATE MODAL ══════════ */}
      <Modal
        open={createOpen}
        title="Add Order"
        subtitle="Prices are calculated from the product prices on the server"
        onClose={() => setCreateOpen(false)}
        footer={(
          <>
            <div className="om-foot-sum">
              <span>{preview.units} {preview.units === 1 ? 'unit' : 'units'}</span>
              <span className="om-foot-total">Est. total <b>{formatPrice(preview.total)}</b></span>
            </div>
            <button className="btn btn-outline" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveCreate} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Create Order'}
            </button>
          </>
        )}
      >
        {formError && <div className="login-error" style={{ marginBottom: 12 }}>{formError}</div>}
        {isError && message && <div className="login-error" style={{ marginBottom: 12 }}>{message}</div>}

        <div className="om-stack">

          <Section icon={User} title="Customer">
            <div className="om-grid">
              <div className="form-field">
                <label>Customer name</label>
                <input className="input" placeholder="Full name" value={form.customer_name}
                  onChange={(e) => setField('customer_name', e.target.value)} />
                {fieldError('customer_name')}
              </div>
              <div className="form-field">
                <label>Phone</label>
                <input className="input" placeholder="10-digit mobile number" value={form.customer_phone}
                  onChange={(e) => setField('customer_phone', e.target.value)} />
                {fieldError('customer_phone')}
              </div>
              <div className="form-field om-full">
                <label>Email <small style={{ color: 'var(--text3)' }}>(use the customer's registered email so the order shows in their account)</small></label>
                <input className="input" type="email" placeholder="name@example.com" value={form.customer_email}
                  onChange={(e) => setField('customer_email', e.target.value)} />
                {fieldError('customer_email')}
              </div>
            </div>
          </Section>

          <Section icon={MapPin} title="Delivery address">
            <div className="om-grid">
              <div className="form-field om-full">
                <label>Address</label>
                <textarea className="input" rows={2} placeholder="House / street / area" value={form.shipping_address}
                  onChange={(e) => setField('shipping_address', e.target.value)} />
              </div>
              <div className="form-field">
                <label>City</label>
                <input className="input" value={form.shipping_city} onChange={(e) => setField('shipping_city', e.target.value)} />
              </div>
              <div className="form-field">
                <label>State</label>
                <input className="input" value={form.shipping_state} onChange={(e) => setField('shipping_state', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Pincode</label>
                <input className="input" value={form.shipping_pincode} onChange={(e) => setField('shipping_pincode', e.target.value)} />
              </div>
            </div>
          </Section>

          <Section icon={Package} title="Products" hint={`${form.items.length} ${form.items.length === 1 ? 'line' : 'lines'}`}>
            {fieldError('items')}
            <div className="om-lines">
              {form.items.map((it, i) => {
                const p = productMap.get(Number(it.product_id))
                const qty = Number(it.quantity) || 0
                const stock = p ? Number(p.stock_qty) : null
                const overStock = p && stock !== null && qty > stock
                const unit = p
                  ? Number(p.price)
                    + (it.customized ? Number(p.customization_price || 0) : 0)
                    + (it.gift_wrap ? Number(p.gift_wrap_price || 0) : 0)
                  : 0
                const canCustomise = p ? !!p.is_customizable : false

                return (
                  <div key={i} className={`om-line${p ? ' has-product' : ''}`}>
                    <div className="om-line-top">
                      <div className="om-line-thumb">
                        {p?.img ? <img src={p.img} alt="" /> : <Package size={18} />}
                      </div>
                      <div className="om-line-select">
                        <Select
                          value={it.product_id}
                          onChange={(v) => setItem(i, 'product_id', v)}
                          options={productOptions}
                          searchable
                          placeholder="— Select product —"
                        />
                        {p && (
                          <div className={`om-line-stock${overStock ? ' warn' : ''}`}>
                            {overStock ? `Only ${stock} in stock` : `${stock} in stock`}
                            {' · '}GST {Number(p.gst_rate || 0)}% {p.price_includes_tax === false ? '(extra)' : '(incl.)'}
                          </div>
                        )}
                      </div>
                      <button type="button" className="btn btn-ghost btn-icon om-line-remove" onClick={() => removeItem(i)}
                        disabled={form.items.length === 1} aria-label="Remove product">
                        <X size={14} />
                      </button>
                    </div>

                    <div className="om-line-bottom">
                      <div className="om-qty">
                        <button type="button" aria-label="Decrease quantity"
                          onClick={() => setItem(i, 'quantity', Math.max(1, qty - 1))}>
                          <Minus size={13} />
                        </button>
                        <input type="number" min={1} value={it.quantity} aria-label="Quantity"
                          onChange={(e) => setItem(i, 'quantity', e.target.value)} />
                        <button type="button" aria-label="Increase quantity"
                          onClick={() => setItem(i, 'quantity', stock ? Math.min(stock, qty + 1) : qty + 1)}>
                          <Plus size={13} />
                        </button>
                      </div>

                      <button
                        type="button"
                        className={`om-toggle${it.customized ? ' on' : ''}`}
                        disabled={!canCustomise}
                        title={p && !canCustomise ? 'This product cannot be personalised' : undefined}
                        aria-pressed={it.customized}
                        onClick={() => setItem(i, 'customized', !it.customized)}
                      >
                        <Sparkles size={13} /> Personalised
                        {p && canCustomise && Number(p.customization_price) > 0 && <em>+{formatPrice(p.customization_price)}</em>}
                      </button>

                      <button
                        type="button"
                        className={`om-toggle${it.gift_wrap ? ' on' : ''}`}
                        aria-pressed={it.gift_wrap}
                        onClick={() => setItem(i, 'gift_wrap', !it.gift_wrap)}
                      >
                        <Gift size={13} /> Gift wrap
                        {p && Number(p.gift_wrap_price) > 0 && <em>+{formatPrice(p.gift_wrap_price)}</em>}
                      </button>

                      <div className="om-line-total">{p ? formatPrice(unit * qty) : '—'}</div>
                    </div>

                    {it.customized && (
                      <input
                        className="input om-line-text"
                        placeholder="Text to print on the product"
                        maxLength={p?.text_max_length || 100}
                        value={it.custom_text}
                        onChange={(e) => setItem(i, 'custom_text', e.target.value)}
                      />
                    )}
                  </div>
                )
              })}
              <button type="button" className="btn btn-outline btn-sm om-add" onClick={addItem}>
                <Plus size={13} /> Add another product
              </button>
            </div>
          </Section>

          <div className="om-two">
            <Section icon={CreditCard} title="Payment & status">
              <div className="om-grid om-grid--1">
                <div className="form-field">
                  <label>Payment method</label>
                  <Select value={form.payment_method} onChange={(v) => setField('payment_method', v)} options={METHOD_OPTIONS} />
                </div>
                <div className="form-field">
                  <label>Order status</label>
                  <Select value={form.status} onChange={(v) => setField('status', v)} options={STATUS_OPTIONS} />
                </div>
              </div>
            </Section>

            <Section icon={Receipt} title="Charges & summary">
              <div className="om-grid">
                <div className="form-field">
                  <label>Shipping (₹)</label>
                  <input type="number" min={0} className="input" value={form.shipping_charge}
                    onChange={(e) => setField('shipping_charge', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Discount (₹)</label>
                  <input type="number" min={0} className="input" value={form.discount}
                    onChange={(e) => setField('discount', e.target.value)} />
                </div>
              </div>
              <SummaryRows
                rows={[
                  { label: 'Subtotal', value: formatPrice(preview.subtotal) },
                  { label: 'Personalisation', value: formatPrice(preview.custom), show: preview.custom > 0 },
                  { label: 'Gift wrap', value: formatPrice(preview.gift), show: preview.gift > 0 },
                  { label: 'Shipping', value: preview.shipping > 0 ? formatPrice(preview.shipping) : 'Free' },
                  { label: 'Discount', value: `−${formatPrice(preview.discount)}`, show: preview.discount > 0 },
                  { label: preview.taxAdded > 0 ? 'GST (extra)' : 'GST (included)', value: formatPrice(preview.tax), show: preview.tax > 0 },
                  { label: 'Saved on MRP', value: formatPrice(preview.savings), show: preview.savings > 0 },
                ]}
                total={formatPrice(preview.total)}
              />
            </Section>
          </div>

          <Section icon={Truck} title="Shipping / courier" hint="optional">
            <div className="om-grid">
              <div className="form-field">
                <label>
                  Courier agency
                  <button type="button" className="om-link-btn" onClick={openManage}>Manage couriers</button>
                </label>
                <Select
                  value={form.courier_agency_id}
                  onChange={(v) => setField('courier_agency_id', v)}
                  options={courierOptions}
                  searchable={courierOptions.length > 8}
                  placeholder="— Select courier —"
                />
                {fieldError('courier_agency_id')}
              </div>
              <div className="form-field">
                <label>Tracking number</label>
                <input className="input" placeholder="Courier tracking number" value={form.tracking_no}
                  onChange={(e) => setField('tracking_no', e.target.value)} />
                {fieldError('tracking_no')}
              </div>
            </div>
          </Section>

          <Section icon={StickyNote} title="Notes">
            <textarea className="input" rows={2} placeholder="Internal notes about this order (optional)" value={form.notes}
              onChange={(e) => setField('notes', e.target.value)} />
          </Section>
        </div>
      </Modal>

      {/* ══════════ MANAGE COURIERS ══════════ */}
      <Modal
        open={manageOpen}
        title="Courier agencies"
        subtitle="Add or remove the agencies you ship with"
        onClose={() => setManageOpen(false)}
        footer={<button className="btn btn-outline" onClick={() => setManageOpen(false)}>Close</button>}
      >
        {courierIsError && courierMessage && (
          <div className="login-error" style={{ marginBottom: 12 }}>{courierMessage}</div>
        )}

        <div className="om-stack">
          <Section icon={Plus} title="Add courier">
            <div className="om-grid">
              <div className="form-field">
                <label>Agency name</label>
                <input className="input" placeholder="e.g. Delhivery" value={newCourier.name}
                  onChange={(e) => setNewCourier({ ...newCourier, name: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Tracking URL <small style={{ color: 'var(--text3)' }}>(optional)</small></label>
                <input className="input" placeholder="https://.../track/{tracking_no}" value={newCourier.tracking_url}
                  onChange={(e) => setNewCourier({ ...newCourier, tracking_url: e.target.value })} />
              </div>
            </div>
            <button type="button" className="btn btn-primary btn-sm" style={{ marginTop: '10px' }} onClick={addCourier}
              disabled={courierSaving || !newCourier.name.trim()}>
              <Plus size={13} /> Add courier
            </button>
          </Section>

          <Section icon={Truck} title="Your couriers" hint={`${couriers.length}`}>
            {couriers.length === 0 && <div className="om-muted">No couriers added yet</div>}
            <div className="om-items">
              {couriers.map((c) => (
                <div key={c.id} className="om-item">
                  <div className="om-item-main">
                    <div className="om-item-name">{c.name}</div>
                    <div className="om-item-sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.tracking_url || 'No tracking link'}
                    </div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-icon" onClick={() => removeCourier(c)}
                    disabled={courierSaving} aria-label={`Delete ${c.name}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete order"
        message={`Delete ${active?.order_no || 'selected order'}? Stock of its items will be restored.`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        textarea.input { resize: vertical; min-height: 56px; }

        .pg-header {
          display: flex; align-items: center; justify-content: space-between;
          gap: var(--space-md); flex-wrap: wrap;
        }

        .tb-search {
          flex: 1 1 260px; min-width: 0; margin: 0;
          display: flex; align-items: center; gap: 10px;
          height: 38px; padding: 0 12px; cursor: text;
          border: 1px solid var(--border); border-radius: 10px;
          background: #f6f8ff; color: var(--text3);
          transition: border-color .18s ease, box-shadow .18s ease;
        }
        .tb-search:focus-within {
          border-color: var(--accent); box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.16);
        }
        .tb-search svg { flex-shrink: 0; }
        .tb-search input {
          flex: 1; min-width: 0; height: 100%; padding: 0; margin: 0;
          border: 0; outline: 0; box-shadow: none; background: transparent;
          font: inherit; font-size: var(--fs-sm); color: var(--text1);
        }
        .tb-search input::placeholder { color: var(--text3); }

        .login-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5; font-size: 0.875rem;
          padding: 0.6rem 0.9rem; border-radius: 10px;
        }

        /* payment / order status dropdown (table) */
        .pay-select {
          appearance: none; -webkit-appearance: none; cursor: pointer;
          border: 0; outline: 0; font: inherit; font-size: var(--fs-xs); font-weight: 600;
          padding: 4px 10px; text-transform: capitalize; text-align: center;
        }
        .pay-select:disabled { opacity: .55; cursor: not-allowed; }

        .st-btn {
          display: inline-flex; align-items: center; gap: 6px; border: 0; cursor: pointer;
          font: inherit; font-size: var(--fs-xs); font-weight: 600; text-transform: capitalize;
          padding: 4px 8px 4px 9px; white-space: nowrap;
          transition: filter .15s ease, box-shadow .15s ease;
        }
        .st-btn:hover:not(:disabled), .st-btn.open { filter: brightness(1.06); box-shadow: 0 0 0 3px rgba(79, 70, 229, .16); }
        .st-btn:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
        .st-btn:disabled { opacity: .6; cursor: not-allowed; padding-right: 10px; }
        .st-chev { transition: transform .18s ease; opacity: .75; }
        .st-btn.open .st-chev { transform: rotate(180deg); }

        .st-menu {
          position: fixed; z-index: 10000; min-width: 200px; padding: 6px;
          border: 1px solid var(--border); border-radius: 12px;
          background: var(--bg-raised, #fff);
          box-shadow: 0 16px 38px rgba(15, 23, 42, .22);
          animation: stIn .14s ease;
        }
        @keyframes stIn { from { opacity: 0; margin-top: -4px; } to { opacity: 1; margin-top: 0; } }
        .st-menu-head {
          padding: 6px 10px 8px; font-size: 11px; font-weight: 700; letter-spacing: .06em;
          text-transform: uppercase; color: var(--text3);
        }
        .st-opt {
          display: flex; align-items: center; gap: 10px; width: 100%; padding: 7px 10px;
          border: 0; border-radius: 8px; background: transparent; cursor: pointer; text-align: left;
          font: inherit; font-size: var(--fs-sm); color: var(--text1); text-transform: capitalize;
        }
        .st-opt:hover, .st-opt:focus-visible { background: var(--bg-hover); outline: 0; }
        .st-opt.current { font-weight: 700; }
        .st-opt.danger { color: #ef4444; }
        .st-ic { width: 24px; height: 24px; border-radius: 7px; display: grid; place-items: center; flex-shrink: 0; }
        .st-check { margin-left: auto; color: var(--accent-hover, #4F46E5); }

        .om-stack { display: flex; flex-direction: column; gap: 14px; }

        .om-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
        .om-grid--1 { grid-template-columns: 1fr; }
        .om-grid > .form-field, .om-two > .om-sec { min-width: 0; }
        .om-full { grid-column: 1 / -1; }
        .om-two { display: grid; grid-template-columns: repeat(auto-fit, minmax(270px, 1fr)); gap: 14px; }

        .om-sec {
          border: 1px solid var(--border); border-radius: var(--radius-lg, 12px);
          background: var(--bg-card, transparent); padding: 14px 16px;
        }
        .om-sec--update { background: var(--bg-raised); border-color: var(--border-active, var(--border)); }
        .om-sec-title {
          display: flex; align-items: center; gap: 8px; margin: 0 0 12px;
          font-size: var(--fs-sm); font-weight: 700; color: var(--text1);
        }
        .om-sec-title small { margin-left: auto; font-weight: 500; font-size: var(--fs-xs); color: var(--text3); }
        .om-sec-ic {
          width: 24px; height: 24px; border-radius: 7px; flex-shrink: 0;
          display: grid; place-items: center;
          background: rgba(79, 70, 229, .12); color: var(--accent-hover, #4F46E5);
        }

        .om-hero {
          display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
          padding: 14px 16px; border-radius: var(--radius-lg, 12px);
          border: 1px solid var(--border-active, var(--border));
          background: linear-gradient(135deg, rgba(79, 70, 229, .12), rgba(6, 182, 212, .07));
        }
        .om-hero-badges { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .om-hero-method { font-size: var(--fs-xs); color: var(--text3); }
        .om-hero-total { text-align: right; line-height: 1.2; }
        .om-hero-total small { display: block; font-size: var(--fs-xs); color: var(--text3); }
        .om-hero-total b { font-size: 22px; color: var(--text1); }

        .om-items { display: flex; flex-direction: column; gap: 8px; }
        .om-item {
          display: flex; align-items: center; gap: 12px; padding: 10px;
          border: 1px solid var(--border); border-radius: var(--radius-md); background: var(--bg-raised);
        }
        .om-item img, .om-noimg {
          width: 48px; height: 48px; border-radius: var(--radius-md); object-fit: cover; flex-shrink: 0;
          background: var(--bg-hover); display: grid; place-items: center; color: var(--text3);
        }
        .om-item-main { flex: 1; min-width: 0; }
        .om-item-name { font-weight: 600; color: var(--text1); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .om-item-sub { font-size: var(--fs-xs); color: var(--text3); margin-top: 1px; }
        .om-item-total { font-weight: 700; color: var(--text1); white-space: nowrap; }
        .om-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
        .om-chip {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; color: var(--text2); padding: 2px 8px; border-radius: 99px;
          border: 1px solid var(--border); background: var(--bg-card, transparent);
        }

        .om-info { display: flex; flex-direction: column; gap: 6px; font-size: var(--fs-sm); color: var(--text2); line-height: 1.5; }
        .om-info-name { font-weight: 700; color: var(--text1); }
        .om-info-row { display: flex; align-items: center; gap: 8px; color: var(--text2); word-break: break-all; }
        .om-info-row svg { color: var(--text3); flex-shrink: 0; }
        .om-muted { color: var(--text3); }

        .om-sum { display: flex; flex-direction: column; gap: 7px; font-size: var(--fs-sm); color: var(--text2); margin-top: 12px; }
        .om-sec > .om-sum:first-of-type { margin-top: 0; }
        .om-sum-row { display: flex; justify-content: space-between; gap: 12px; }
        .om-sum-total {
          margin-top: 2px; padding-top: 9px; border-top: 1px solid var(--border);
          font-weight: 800; font-size: var(--fs-md, 15px); color: var(--text1);
        }

        .om-lines { display: flex; flex-direction: column; gap: 10px; }
        .om-line {
          border: 1px solid var(--border); border-radius: var(--radius-md);
          background: var(--bg-raised); padding: 10px 12px;
          display: flex; flex-direction: column; gap: 10px;
        }
        .om-line.has-product { border-color: var(--border-active, var(--border)); }
        .om-line-top { display: flex; align-items: flex-start; gap: 10px; }
        .om-line-thumb {
          width: 44px; height: 44px; border-radius: var(--radius-md); flex-shrink: 0; overflow: hidden;
          display: grid; place-items: center; background: var(--bg-hover); color: var(--text3);
        }
        .om-line-thumb img { width: 100%; height: 100%; object-fit: cover; }
        .om-line-select { flex: 1; min-width: 0; }
        .om-line-stock { font-size: 11px; color: var(--text3); margin-top: 4px; }
        .om-line-stock.warn { color: #ef4444; font-weight: 600; }
        .om-line-remove { flex-shrink: 0; }

        .om-line-bottom { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .om-line-total { margin-left: auto; font-weight: 800; color: var(--text1); white-space: nowrap; }
        .om-line-text { width: 100%; }

        .om-qty {
          display: inline-flex; align-items: center; height: 32px;
          border: 1px solid var(--border); border-radius: 8px; background: var(--bg-card, transparent); overflow: hidden;
        }
        .om-qty button {
          width: 30px; height: 100%; border: 0; background: transparent; color: var(--text2);
          display: grid; place-items: center; cursor: pointer;
        }
        .om-qty button:hover { background: var(--bg-hover); }
        .om-qty input {
          width: 44px; height: 100%; padding: 0; text-align: center; border: 0; outline: 0;
          background: transparent; font: inherit; font-size: var(--fs-sm); color: var(--text1); -moz-appearance: textfield;
        }
        .om-qty input::-webkit-outer-spin-button, .om-qty input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

        .om-toggle {
          display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 12px;
          border: 1px solid var(--border); border-radius: 99px; background: var(--bg-card, transparent);
          color: var(--text2); font: inherit; font-size: var(--fs-xs); font-weight: 600; cursor: pointer;
          transition: background .15s, border-color .15s, color .15s;
        }
        .om-toggle em { font-style: normal; font-weight: 500; color: var(--text3); }
        .om-toggle:hover:not(:disabled) { border-color: var(--accent); }
        .om-toggle.on { background: rgba(79, 70, 229, .12); border-color: var(--accent); color: var(--accent-hover, #4F46E5); }
        .om-toggle.on em { color: inherit; }
        .om-toggle:disabled { opacity: .45; cursor: not-allowed; }

        .om-add { align-self: flex-start; }

        .om-foot-sum {
          margin-right: auto; display: flex; align-items: center; gap: 12px;
          font-size: var(--fs-xs); color: var(--text3);
        }
        .om-foot-total b { font-size: var(--fs-md, 15px); color: var(--text1); margin-left: 4px; }

        .om-link-btn {
          margin-left: 8px; border: 0; background: transparent; cursor: pointer; padding: 0;
          font: inherit; font-size: var(--fs-xs); font-weight: 600; color: var(--accent-hover, #4F46E5);
        }
        .om-link-btn:hover { text-decoration: underline; }
        .om-track-link {
          display: inline-flex; align-items: center; gap: 5px; margin-top: 6px; width: fit-content;
          font-size: var(--fs-xs); font-weight: 600; color: var(--accent-hover, #4F46E5); text-decoration: none;
        }
        .om-track-link:hover { text-decoration: underline; }

        /* customisation details (admin order view) */
        .om-cust {
          margin-top: 10px; padding: 10px 12px; border-radius: var(--radius-md);
          border: 1px dashed var(--border-active, var(--border)); background: var(--bg-card, transparent);
        }
        .om-cust-head {
          display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
          font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--accent-hover, #4F46E5);
        }
        .om-cust-figs { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 10px; }
        .om-cust-fig { margin: 0; width: 160px; }
        .om-cust-fig figcaption { margin-top: 4px; font-size: 11px; color: var(--text3); word-break: break-all; }
        .om-cust-fig figcaption a { color: var(--accent-hover, #4F46E5); font-weight: 600; }
        .om-cust-stage {
          position: relative; display: block; width: 160px; height: 160px; overflow: hidden;
          border: 1px solid var(--border); border-radius: var(--radius-md); background: #fff;
        }
        .om-cust-stage img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .om-cust-text {
          position: absolute; transform: translate(-50%, -50%); max-width: 90%;
          font-weight: 700; line-height: 1.1; text-align: center; white-space: pre-wrap; word-break: break-word;
          text-shadow: 0 0 2px rgba(255, 255, 255, .55);
        }
        .om-cust-rows { display: flex; flex-direction: column; gap: 4px; }
        .om-cust-row { display: flex; gap: 10px; font-size: var(--fs-xs); line-height: 1.5; }
        .om-cust-row span { flex: 0 0 38%; max-width: 220px; color: var(--text3); }
        .om-cust-row b { flex: 1; min-width: 0; color: var(--text1); font-weight: 600; word-break: break-word; }
        .om-cust-sw {
          display: inline-block; width: 11px; height: 11px; border-radius: 50%; margin: 0 4px 0 8px;
          vertical-align: -1px; border: 1px solid var(--border);
        }
      `}</style>
    </div>
  )
}